import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { getStockValue, saveStockValue, undoStockValue } from '../../api/purchase'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { useToast } from '../../components/Toast.jsx'
import { useSession } from '../../session'
import { fmtDateTime, fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money } from '../sales/salesShared'
import { monthName, recentMonths } from '../accounting/accountingShared'
import PurchaseTabs from './PurchaseTabs'

// The month-end stock value, on plans without Inventory (the API's
// stock_value.py): stock isn't counted as it moves, so goods received sit in
// the Inventory account until each month's end, when what's left is valued
// at cost and the rest goes to cost of goods sold. Also shown in Month-end
// close (StockValueForm).

const HOW = 'Your plan doesn’t count stock as it moves, so goods you receive are kept in Inventory until the month ends. Then count what’s left and enter its value at cost: the rest was used or sold, and moves to cost of goods sold in one entry on the month’s last day. This is the periodic method.'

/** What the books hold, what was counted and the entry it made, with the
    value to enter (or change, or take out). data: the API's month summary. */
export function StockValueForm({ data, onSaved }) {
  const { notify } = useToast()
  const { has, readOnly } = useSession()
  const entered = data.entered
  const [editing, setEditing] = useState(!entered)
  const [value, setValue] = useState(entered ? String(entered.value) : '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    setEditing(!data.entered || data.out_of_date)
    setValue(data.entered ? String(data.entered.value) : '')
    setError('')
  }, [data.period_ym, data.entered?.value, data.out_of_date]) // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, done) => {
    setBusy(true)
    setError('')
    try {
      const res = await fn()
      notify(done)
      onSaved?.(res)
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'That didn’t work.')
    } finally {
      setBusy(false)
    }
  }
  const save = (e) => {
    e.preventDefault()
    if (String(value).trim() === '' || !Number.isFinite(Number(String(value).replace(/,/g, '')))) { setError('Enter what the stock is worth, at cost.'); return }
    act(() => saveStockValue(data.period_ym, String(value).replace(/,/g, '')), 'Stock value saved')
  }
  const canWrite = !readOnly && data.ready && !data.closed
  const entryLink = entered?.je_id && (has('accounting')
    ? <Link to={`/accounting/journal/${entered.je_id}`}>{docNo('je', entered.je_no)}</Link>
    : docNo('je', entered.je_no))

  return (
    <div className="sv-form">
      <dl className="acc-tieout">
        <div><dt>{data.accounts?.inventory} Inventory at {fmtDay(data.end)}</dt><dd>{money(data.book_value)}</dd></div>
        {entered && <div><dt>Counted</dt><dd>{money(entered.value)}</dd></div>}
        {entered && <div className="is-diff"><dt>{entered.adjustment >= 0 ? 'Used or sold, to cost of goods sold' : 'Counted above the books, off cost of goods sold'}</dt><dd>{money(Math.abs(entered.adjustment))}</dd></div>}
      </dl>
      {data.out_of_date && <p className="acc-check-more">Something was posted to Inventory after this was entered, so the books hold {money(data.book_value)} now. Enter the value again.</p>}
      {entered && !editing && (
        <p className="acc-check-more">
          {entryLink || 'No entry needed: the books matched'}
          {' · '}Entered {fmtDateTime(entered.entered_at)}{entered.entered_by ? ` by ${entered.entered_by}` : ''}
          {canWrite && <span data-write> · <button type="button" className="ci-link-btn" onClick={() => setEditing(true)}>Change</button> · <button type="button" className="ci-link-btn" disabled={busy} onClick={() => act(() => undoStockValue(data.period_ym), 'Stock value taken out')}>{busy ? 'Working…' : 'Undo'}</button></span>}
        </p>
      )}
      {canWrite && editing && (
        <form className="sv-row" onSubmit={save} data-write>
          <label className="ci-form-field">
            <span>What the stock left is worth, at cost</span>
            <input value={value} onChange={(e) => { setValue(e.target.value); setError('') }} inputMode="decimal" placeholder="0.00" aria-label="Stock value" />
          </label>
          <button type="submit" className="ci-btn ci-btn-primary ci-btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          {entered && !data.out_of_date && <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => { setEditing(false); setError('') }}>Cancel</button>}
        </form>
      )}
      {!data.ready && <p className="acc-check-more">{data.month} is still running. You can enter its stock value from {fmtDay(data.end)}.</p>}
      {data.closed && !entered && <p className="acc-check-more">{data.month} is closed.</p>}
      {error && <p className="acc-field-error" role="alert">{error}</p>}
    </div>
  )
}

export default function StockValue() {
  const [params, setParams] = useSearchParams()
  const asked = params.get('month')
  const [ym, setYm] = useState(/^\d{4}-\d{2}$/.test(asked || '') ? asked : '')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const months = recentMonths(18)

  useEffect(() => {
    setLoading(true)
    setError('')
    getStockValue(ym || undefined)
      .then((d) => { setData(d); if (!ym && d?.period_ym) setYm(d.period_ym) })
      .catch((e) => setError(e.message || 'Couldn’t load the stock value.'))
      .finally(() => setLoading(false))
  }, [ym]) // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (value) => {
    setYm(value)
    setParams((p) => { const n = new URLSearchParams(p); n.set('month', value); return n }, { replace: true })
  }

  return (
    <div className="ci-page">
      <PurchaseTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Stock value <HelpTip text={HOW} /></h1>
            <div className="ci-title-meta">At each month’s end, what the goods you still have are worth. What was used or sold goes to cost of goods sold.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <select className="ci-select" value={ym} onChange={(e) => choose(e.target.value)} aria-label="Month">
            {!ym && <option value="">Choose a month…</option>}
            {months.map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
          </select>
        </div>
      </div>
      {error && <ErrorBanner message={error} />}
      {loading || !data ? <Spinner label="Loading the stock value…" /> : !data.applies ? (
        <p className="doc-empty">Your plan counts stock as it moves (Inventory), so there’s nothing to enter here. <Link to="/inventory/stock">Open stock</Link></p>
      ) : (
        <>
          <section className="doc-card">
            <div className="doc-card-head"><h2>Stock at the end of {data.month}</h2><span>{data.entered ? (data.out_of_date ? 'Enter it again' : 'Entered') : (data.needed ? 'To enter' : 'Nothing on the books')}</span></div>
            <div className="sv-body">
              <StockValueForm data={data} onSaved={setData} />
            </div>
          </section>
          <section className="doc-card">
            <div className="doc-card-head"><h2>Months entered</h2><span>{data.months?.length ? `${data.months.length} shown` : 'None yet'}</span></div>
            {data.months?.length ? (
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead><tr><th>Month</th><th className="num">Stock counted</th><th className="num">To cost of goods sold</th></tr></thead>
                  <tbody>
                    {data.months.map((m) => (
                      <tr key={m.period_ym}>
                        <td><button type="button" className="acc-link-btn" onClick={() => choose(m.period_ym)}>{monthName(m.period_ym)}</button></td>
                        <td className="num">{money(m.value)}</td>
                        <td className="num">{money(m.adjustment)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="doc-empty">No month’s stock value has been entered yet.</p>}
          </section>
        </>
      )}
    </div>
  )
}
